import { pendingNavHrefAtom } from '@/atoms/sideNavAtoms';
import { OfflineGatedTooltip } from '@/components/offline-recording/offline-gated-tooltip';
import { isNavItemAvailableOffline } from '@/components/offline-recording/offline-nav-availability';
import { useOfflineGating } from '@/hooks/desktop/useOfflineGating';
import { cn } from '@/lib/cn';
import { DropdownMenuItem } from '@heidi/design-system/dropdown-menu';
import { FramedIcon, type FramedIconElement } from '@heidi/design-system/framed-icon';
import { useSetAtom } from 'jotai';
import Link from 'next/link';

import { type FooterMoreRowDescriptor } from './footer-rail-context';

/**
 * One collapsed footer item as a More-dropdown row. Re-applies the offline gate the
 * rail buttons get via their own render — a dropdown row bypasses that path. Footer
 * items aren't in the offline allowlist, so they gate off whenever controls are gated.
 */
export function FooterRailMoreRow({
  descriptor,
}: {
  descriptor: FooterMoreRowDescriptor;
}) {
  const { label, icon, href, external, onSelect, isActive, testId } = descriptor;
  const setPendingNavHref = useSetAtom(pendingNavHrefAtom);

  const { shouldGateControls } = useOfflineGating();
  const isOfflineGated = shouldGateControls && !isNavItemAvailableOffline(testId);

  const className = cn(
    'gap-2.5 py-2',
    isActive && 'bg-surface-pressed hover:bg-surface-pressed focus:bg-surface-pressed'
  );

  // Match the sidebar nav tile's hover/selected fill (SidebarMenuButton). An active
  // row stays pressed (no hover swap). Every fill has a newBrandDark: twin because
  // FramedIcon's neutral recipe carries its own dark fill (newBrandDark:bg-surface-elevated)
  // that would otherwise outrank a plain override in dark mode.
  const framedIconClassName = cn(
    '[&_svg]:text-text-secondary',
    isActive
      ? [
          'bg-background-secondary-pressed',
          'newBrandDark:bg-background-secondary-pressed',
          '[&_svg]:text-text-primary',
        ]
      : [
          'group-hover/dropdown-menu-item:bg-background-secondary-hover',
          'newBrandDark:group-hover/dropdown-menu-item:bg-background-secondary-hover',
          'group-focus/dropdown-menu-item:bg-background-secondary-hover',
          'newBrandDark:group-focus/dropdown-menu-item:bg-background-secondary-hover',
        ]
  );

  const content = (
    <>
      <FramedIcon
        size="sm"
        state="neutral"
        className={framedIconClassName}
        icon={icon as FramedIconElement}
      />
      <span>{label}</span>
    </>
  );

  if (isOfflineGated) {
    return (
      // `role="group"` keeps the trigger span a legal child of the surrounding
      // Radix `menu`, which otherwise rejects it between `menu` and `menuitem`.
      <OfflineGatedTooltip
        isOfflineGated
        role="group"
        className="block cursor-not-allowed"
      >
        <DropdownMenuItem
          disabled
          className={className}
          data-testid={testId}
          // Radix would otherwise steal focus/close on a disabled item.
          onSelect={(event) => event.preventDefault()}
        >
          {content}
        </DropdownMenuItem>
      </OfflineGatedTooltip>
    );
  }

  if (href && external) {
    return (
      <DropdownMenuItem asChild className={className}>
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => onSelect?.()}
          data-testid={testId}
        >
          {content}
        </a>
      </DropdownMenuItem>
    );
  }

  if (href) {
    return (
      <DropdownMenuItem asChild className={className}>
        <Link
          href={href}
          // No viewport prefetch; Next still prefetches on hover.
          prefetch={false}
          onClick={() => {
            setPendingNavHref(href);
            onSelect?.();
          }}
          data-testid={testId}
        >
          {content}
        </Link>
      </DropdownMenuItem>
    );
  }

  return (
    <DropdownMenuItem className={className} onClick={onSelect} data-testid={testId}>
      {content}
    </DropdownMenuItem>
  );
}
